import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { changePassword, changeEmail } from 'services/employeePortal';
import axiosInstance from '../../axios/axiosInstance';

const INPUT_STYLE = {
    border: '1px solid #ccc',
    borderRadius: 8,
    padding: '10px 40px 10px 14px',
    width: '100%',
    fontSize: 14,
    outline: 'none',
};

const PasswordInput = ({ label, value, onChange, placeholder }) => {
    const [show, setShow] = useState(false);
    return (
        <div className="col-md-4">
            <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block' }}>{label}</label>
            <div style={{ position: 'relative' }}>
                <input
                    type={show ? 'text' : 'password'}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    style={INPUT_STYLE}
                />
                <span
                    onClick={() => setShow(s => !s)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', fontSize: 16 }}
                >
                    {show ? '🔓' : '🔒'}
                </span>
            </div>
        </div>
    );
};

const Account = () => {
    const [activeTab, setActiveTab] = useState('password');
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [confirmEmail, setConfirmEmail] = useState('');
    const [message, setMessage] = useState(null);
    const [loading, setLoading] = useState(false);

    const currentEmail = localStorage.getItem('userEmail') || '';

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setMessage({ type: 'error', text: 'New passwords do not match.' });
            return;
        }
        if (newPassword.length < 8) {
            setMessage({ type: 'error', text: 'New password must be at least 8 characters.' });
            return;
        }
        setLoading(true);
        try {
            await changePassword({ email: currentEmail, oldPassword, newPassword });
            setMessage({ type: 'success', text: 'Password changed successfully.' });
            setOldPassword(''); setNewPassword(''); setConfirmPassword('');
        } catch (err) {
            const msg = err?.response?.data?.message || 'Failed to change password. Check your current password.';
            setMessage({ type: 'error', text: msg });
        } finally {
            setLoading(false);
        }
    };

    const handleChangeEmail = async (e) => {
        e.preventDefault();
        if (newEmail !== confirmEmail) {
            setMessage({ type: 'error', text: 'Email addresses do not match.' });
            return;
        }
        setLoading(true);
        try {
            await changeEmail({ currentEmail, newEmail });
            localStorage.setItem('userEmail', newEmail);
            setMessage({ type: 'success', text: 'Email updated successfully.' });
            setNewEmail(''); setConfirmEmail('');
        } catch (err) {
            const msg = err?.response?.data?.message || 'Failed to change email. Please try again.';
            setMessage({ type: 'error', text: msg });
        } finally {
            setLoading(false);
        }
    };

    // ---- MFA state ----
    const [mfaEnabled, setMfaEnabled] = useState(
        () => localStorage.getItem('mfa_enabled') === 'true'
    );
    const [mfaStep, setMfaStep] = useState('idle'); // idle | setup | verify | disable
    const [mfaQrUrl, setMfaQrUrl] = useState('');
    const [mfaOtpUri, setMfaOtpUri] = useState('');
    const [mfaSecret, setMfaSecret] = useState('');
    const [mfaCode, setMfaCode] = useState('');
    const [mfaLoading, setMfaLoading] = useState(false);
    const [mfaMsg, setMfaMsg] = useState(null);

    const handleMfaSetup = async () => {
        setMfaLoading(true); setMfaMsg(null);
        try {
            const res = await axiosInstance.post('/auth/mfa/setup', { email: currentEmail });
            setMfaQrUrl(res.data?.qrCodeUrl || '');
            setMfaOtpUri(res.data?.otpauthUri || '');
            setMfaSecret(res.data?.secret || '');
            setMfaStep('setup');
        } catch (err) {
            setMfaMsg({ type: 'error', text: err?.response?.data?.message || 'Failed to initiate MFA setup. Please try again.' });
        } finally {
            setMfaLoading(false);
        }
    };

    const handleMfaVerify = async () => {
        setMfaLoading(true); setMfaMsg(null);
        try {
            await axiosInstance.post('/auth/mfa/verify', { email: currentEmail, code: mfaCode, secret: mfaSecret });
            setMfaEnabled(true);
            localStorage.setItem('mfa_enabled', 'true');
            setMfaStep('idle');
            setMfaCode('');
            setMfaMsg({ type: 'success', text: 'Multi-factor authentication has been enabled on your account.' });
        } catch (err) {
            setMfaMsg({ type: 'error', text: err?.response?.data?.message || 'Invalid code. Please check your authenticator app and try again.' });
        } finally {
            setMfaLoading(false);
        }
    };

    const handleMfaDisable = async () => {
        setMfaLoading(true); setMfaMsg(null);
        try {
            await axiosInstance.post('/auth/mfa/disable', { email: currentEmail, code: mfaCode });
            setMfaEnabled(false);
            localStorage.setItem('mfa_enabled', 'false');
            setMfaStep('idle');
            setMfaCode('');
            setMfaMsg({ type: 'success', text: 'Multi-factor authentication has been disabled.' });
        } catch (err) {
            setMfaMsg({ type: 'error', text: err?.response?.data?.message || 'Failed to disable MFA. Check your code and try again.' });
        } finally {
            setMfaLoading(false);
        }
    };

    const tabStyle = (tab) => ({
        padding: '10px 20px',
        border: 'none',
        borderBottom: activeTab === tab ? '2px solid #e87722' : '2px solid transparent',
        background: 'none',
        color: activeTab === tab ? '#e87722' : '#6c757d',
        fontWeight: activeTab === tab ? 700 : 400,
        cursor: 'pointer',
        fontSize: 14,
    });

    return (
        <div style={{ padding: '24px', backgroundColor: '#F8F9FA', minHeight: '100vh' }}>
            <h4 style={{ color: '#1a9fbb', fontWeight: 700, marginBottom: 4 }}>Account</h4>
            <p style={{ color: '#6c757d', marginBottom: 24, fontSize: 14 }}>
                Manage your account settings—update your password for added security and change your email to stay informed.
            </p>

            <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                {/* Tabs */}
                <div style={{ borderBottom: '1px solid #e0e0e0', marginBottom: 24 }}>
                    <button style={tabStyle('password')} onClick={() => { setActiveTab('password'); setMessage(null); }}>
                        Change Password
                    </button>
                    <button style={tabStyle('email')} onClick={() => { setActiveTab('email'); setMessage(null); }}>
                        Change Email
                    </button>
                    <button style={tabStyle('mfa')} onClick={() => { setActiveTab('mfa'); setMessage(null); setMfaMsg(null); setMfaStep('idle'); }}>
                        Two-Factor Auth (MFA)
                    </button>
                </div>

                {/* Alert */}
                {message && (
                    <div style={{
                        padding: '10px 16px',
                        borderRadius: 8,
                        marginBottom: 20,
                        background: message.type === 'success' ? '#d4edda' : '#f8d7da',
                        color: message.type === 'success' ? '#155724' : '#721c24',
                        fontSize: 14,
                    }}>
                        {message.text}
                    </div>
                )}

                {/* Change Password Tab */}
                {activeTab === 'password' && (
                    <form onSubmit={handleChangePassword}>
                        <h5 style={{ fontWeight: 700, marginBottom: 4 }}>Change password</h5>
                        <p style={{ color: '#6c757d', fontSize: 13, marginBottom: 20 }}>
                            Update your password to a password you have not previously used.
                        </p>
                        <div className="row g-3 mb-4">
                            <PasswordInput label="Old Password" value={oldPassword} onChange={e => setOldPassword(e.target.value)} placeholder="Enter old password" />
                            <PasswordInput label="New Password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password" />
                            <PasswordInput label="Confirm New Password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Enter new password" />
                        </div>

                        <div style={{ marginBottom: 24 }}>
                            <p style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Password Requirements</p>
                            {[
                                'Must contain at least 1 number',
                                'Must contain at least 1 upper-case letter',
                                'Must contain at least 1 lower-case letter',
                                'Must contain at least 1 non-alphanumeric character (e.g. !@#$)',
                                'Must contain at least 8 characters',
                            ].map((req, i) => (
                                <p key={i} style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>{i + 1}. {req}</p>
                            ))}
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            style={{ background: '#e87722', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 32px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
                        >
                            {loading ? 'Saving...' : 'Change Password'}
                        </button>
                    </form>
                )}

                {/* Change Email Tab */}
                {activeTab === 'email' && (
                    <form onSubmit={handleChangeEmail}>
                        <h5 style={{ fontWeight: 700, marginBottom: 4 }}>Change Email</h5>
                        <p style={{ color: '#6c757d', fontSize: 13, marginBottom: 20 }}>
                            Update your email address to keep your account information current.
                        </p>
                        <div className="row g-3 mb-4">
                            <div className="col-md-4">
                                <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block' }}>New Email</label>
                                <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="Enter new email" style={INPUT_STYLE} required />
                            </div>
                            <div className="col-md-4">
                                <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block' }}>Confirm New Email</label>
                                <input type="email" value={confirmEmail} onChange={e => setConfirmEmail(e.target.value)} placeholder="Confirm new email" style={INPUT_STYLE} required />
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            style={{ background: '#e87722', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 32px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
                        >
                            {loading ? 'Saving...' : 'Change Email'}
                        </button>
                    </form>
                )}
                {/* MFA Tab */}
                {activeTab === 'mfa' && (
                    <div>
                        <h5 style={{ fontWeight: 700, marginBottom: 4 }}>Two-Factor Authentication (MFA)</h5>
                        <p style={{ color: '#6c757d', fontSize: 13, marginBottom: 20 }}>
                            Add an extra layer of security to your account using an authenticator app (Google Authenticator, Authy, etc.).
                        </p>

                        {mfaMsg && (
                            <div style={{
                                padding: '10px 16px', borderRadius: 8, marginBottom: 20,
                                background: mfaMsg.type === 'success' ? '#d4edda' : '#f8d7da',
                                color: mfaMsg.type === 'success' ? '#155724' : '#721c24', fontSize: 14,
                            }}>
                                {mfaMsg.text}
                            </div>
                        )}

                        {/* MFA Status Badge */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, padding: '16px 20px', borderRadius: 10, background: mfaEnabled ? 'rgba(40,167,69,0.06)' : 'rgba(220,53,69,0.06)', border: `1px solid ${mfaEnabled ? 'rgba(40,167,69,0.2)' : 'rgba(220,53,69,0.2)'}` }}>
                            <span style={{ fontSize: 32 }}>{mfaEnabled ? '🔐' : '🔓'}</span>
                            <div>
                                <p style={{ fontWeight: 700, margin: 0, fontSize: 15, color: mfaEnabled ? '#28a745' : '#dc3545' }}>
                                    MFA is currently {mfaEnabled ? 'Enabled' : 'Disabled'}
                                </p>
                                <p style={{ margin: 0, fontSize: 13, color: '#6c757d' }}>
                                    {mfaEnabled
                                        ? 'Your account is protected with two-factor authentication.'
                                        : 'Enable MFA to protect your account with an additional verification step.'}
                                </p>
                            </div>
                        </div>

                        {/* Setup Flow */}
                        {!mfaEnabled && mfaStep === 'idle' && (
                            <button
                                onClick={handleMfaSetup}
                                disabled={mfaLoading}
                                style={{ background: '#e87722', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 32px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
                            >
                                {mfaLoading ? 'Setting up...' : 'Enable MFA'}
                            </button>
                        )}

                        {mfaStep === 'setup' && (
                            <div>
                                <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Step 1: Scan the QR code with your authenticator app</p>
                                {mfaOtpUri ? (
                                    <div style={{ display: 'inline-block', padding: 12, background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, marginBottom: 16 }}>
                                        <QRCodeSVG value={mfaOtpUri} size={180} />
                                    </div>
                                ) : (
                                    <div style={{ marginBottom: 16 }}>
                                        <p style={{ fontSize: 13, color: '#6c757d' }}>Manual entry key:</p>
                                        <code style={{ background: '#f8f9fa', padding: '6px 12px', borderRadius: 6, fontSize: 13, letterSpacing: 2, display: 'inline-block' }}>{mfaSecret}</code>
                                    </div>
                                )}
                                <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Step 2: Enter the 6-digit code from your app</p>
                                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                    <input
                                        type="text"
                                        maxLength={6}
                                        value={mfaCode}
                                        onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                                        placeholder="000000"
                                        style={{ ...INPUT_STYLE, width: 140, textAlign: 'center', letterSpacing: 6, fontFamily: 'monospace', fontSize: 20, fontWeight: 700 }}
                                    />
                                    <button
                                        onClick={handleMfaVerify}
                                        disabled={mfaLoading || mfaCode.length !== 6}
                                        style={{ background: '#28a745', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
                                    >
                                        {mfaLoading ? 'Verifying...' : 'Verify & Enable'}
                                    </button>
                                    <button
                                        onClick={() => { setMfaStep('idle'); setMfaCode(''); }}
                                        style={{ background: '#6c757d', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer' }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Disable Flow */}
                        {mfaEnabled && mfaStep === 'idle' && (
                            <button
                                onClick={() => setMfaStep('disable')}
                                style={{ background: '#dc3545', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 32px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
                            >
                                Disable MFA
                            </button>
                        )}

                        {mfaStep === 'disable' && (
                            <div>
                                <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Enter your authenticator code to disable MFA:</p>
                                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                    <input
                                        type="text"
                                        maxLength={6}
                                        value={mfaCode}
                                        onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                                        placeholder="000000"
                                        style={{ ...INPUT_STYLE, width: 140, textAlign: 'center', letterSpacing: 6, fontFamily: 'monospace', fontSize: 20, fontWeight: 700 }}
                                    />
                                    <button
                                        onClick={handleMfaDisable}
                                        disabled={mfaLoading || mfaCode.length !== 6}
                                        style={{ background: '#dc3545', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
                                    >
                                        {mfaLoading ? 'Disabling...' : 'Confirm Disable'}
                                    </button>
                                    <button
                                        onClick={() => { setMfaStep('idle'); setMfaCode(''); }}
                                        style={{ background: '#6c757d', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer' }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        <div style={{ marginTop: 24, padding: '14px 16px', borderRadius: 8, background: 'rgba(26,159,187,0.05)', border: '1px solid rgba(26,159,187,0.15)', fontSize: 12, color: '#555' }}>
                            <strong>Recommended authenticator apps:</strong> Google Authenticator, Authy, Microsoft Authenticator, 1Password.
                            Once enabled, you will be prompted for a 6-digit code on each login.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Account;
